import { type TechlineCaseEntity } from '@packages/domain/techline/cases';
import { Typography } from '@packages/ui/shared';
import { StatusBadgeGroup } from '@web/components/status-badge-group';
import { formatNumberWithCommas, getDisplayDate } from '@web/utils';
import { createUseStyles } from 'react-jss';

import { CollapsibleSection } from './collapsible-section';

const BADGE_LABELS = {
    duplicateConcern: {
        trueDescription: 'Technician was able to duplicate the concern',
        falseDescription: 'Technician was not able to duplicate the concern',
    },
    vehicleBeenInBefore: {
        trueDescription: 'This vehicle has been in before for the same concern',
        falseDescription: 'This vehicle has not been in before for the same concern',
    },
    partsReplaced: {
        trueDescription: 'Parts have been replaced for this or a similar previous concern',
        falseDescription: 'No parts have been replaced for this or a similar previous concern',
    },
} as const;

const useStyles = createUseStyles({
    concernDetailsContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '24px',
    },
    fullWidth: {
        gridColumn: '1 / -1',
    },
});

/**
 * Props for the {@link ConcernDetailsSection} component.
 */
type ConcernDetailsSectionProps = {
    /** The case details data containing concern and vehicle history fields. */
    caseDetails: TechlineCaseEntity | undefined;
    /** Whether to show the customer concern details field. */
    allowCustomerConcernDetails: boolean;
};
/**
 * Displays the case details and vehicle concern information for a submitted techline case.
 * Wraps itself in a {@link CollapsibleSection} so it fully encapsulates its own section chrome.
 * Includes repair order info, mileage, customer concern description, technician description,
 * and vehicle history.
 *
 * @param props - {@link ConcernDetailsSectionProps}
 * @returns the concern details section JSX
 */
export function ConcernDetailsSection({ caseDetails, allowCustomerConcernDetails }: ConcernDetailsSectionProps) {
    const classes = useStyles();

    const mileage = formatNumberWithCommas(caseDetails?.mileage, '--');
    const previousMileage = formatNumberWithCommas(caseDetails?.previousMileage, '--');
    const repairOrderOpenDate = getDisplayDate(caseDetails?.repairOrderOpenDate, undefined, '--');

    return (
        <CollapsibleSection defaultExpanded={true} showDividerTop title="Concern">
            <div className={classes.concernDetailsContent}>
                <div className={classes.grid}>
                    <div className="d-flex flex-column">
                        <Typography variant="label">Repair order number</Typography>
                        <div className="mt-1">
                            <Typography>{caseDetails?.repairOrderNumber || '--'}</Typography>
                        </div>
                    </div>
                    <div className="d-flex flex-column">
                        <Typography variant="label">Repair open date</Typography>
                        <div className="mt-1">
                            <Typography>{repairOrderOpenDate}</Typography>
                        </div>
                    </div>
                    <div className="d-flex flex-column">
                        <Typography variant="label">Mileage</Typography>
                        <div className="mt-1">
                            <Typography>{mileage}</Typography>
                        </div>
                    </div>
                </div>

                {allowCustomerConcernDetails && (
                    <div className="d-flex flex-column">
                        <Typography variant="label">Customer&apos;s concern description</Typography>
                        <div className="mt-1">
                            <Typography>{caseDetails?.customerConcern || '--'}</Typography>
                        </div>
                    </div>
                )}

                <StatusBadgeGroup
                    {...BADGE_LABELS.duplicateConcern}
                    hideWhenFalse
                    status={caseDetails?.hasBeenAbleToDuplicate || false}
                >
                    <div className="d-flex flex-column">
                        <Typography variant="label">Steps completed to duplicate concern</Typography>
                        <div className="mt-1">
                            <Typography>{caseDetails?.technician5StepsBeforeFailureDuplication || '--'}</Typography>
                        </div>
                    </div>
                </StatusBadgeGroup>

                <StatusBadgeGroup
                    {...BADGE_LABELS.vehicleBeenInBefore}
                    hideWhenFalse
                    status={caseDetails?.vehicleHasBeenInBefore || false}
                >
                    <div className={classes.grid}>
                        <div className="d-flex flex-column">
                            <Typography variant="label">Previous repair order number</Typography>
                            <div className="mt-1">
                                <Typography>{caseDetails?.previousRONumber || '--'}</Typography>
                            </div>
                        </div>
                        <div className="d-flex flex-column">
                            <Typography variant="label">Previous mileage</Typography>
                            <div className="mt-1">
                                <Typography>{previousMileage}</Typography>
                            </div>
                        </div>
                        <div className={`d-flex flex-column ${classes.fullWidth}`}>
                            <Typography variant="label">Previous diagnosis and repair</Typography>
                            <div className="mt-1">
                                <Typography>{caseDetails?.previousDiagnosisAndRepairs || '--'}</Typography>
                            </div>
                        </div>
                    </div>
                </StatusBadgeGroup>

                <StatusBadgeGroup
                    {...BADGE_LABELS.partsReplaced}
                    hideWhenFalse
                    status={caseDetails?.werePartsReplaced || false}
                >
                    <div className="d-flex flex-column">
                        <Typography variant="label">Parts replaced</Typography>
                        <div className="mt-1">
                            <Typography>{caseDetails?.whatPartsWereReplaced || '--'}</Typography>
                        </div>
                    </div>
                    <div className="d-flex flex-column">
                        <Typography variant="label">How did the condition improve or change</Typography>
                        <div className="mt-1">
                            <Typography>{caseDetails?.didTheConditionImprove || '--'}</Typography>
                        </div>
                    </div>
                </StatusBadgeGroup>
            </div>
        </CollapsibleSection>
    );
}
